import {BehaviorSubject} from "rxjs";
import {useSubjectSnapshot} from "features/core/subject.hooks";
import {BettingTypesMap, BettingTypeIds} from "features/core/definitions";
import {postCoupon, toSubmitError} from "features/betslip/lib/submit";
import {fobOddsEntry} from "features/betslip/lib/selection-guard";
import {modalServiceSubject} from "services/modal.service";

const FOB_MAX_BET_COUNT = 10;
const FOB_SYSTEM_MIN_BET_COUNT = 2;
const FOB_SYSTEM_MAX_BET_COUNT = 7;

export interface FobSlipItem {
    hippodrome: any;
    bettingType: any;
    run: any;
    oddKey: string;
    horseNos: string[];
}

/**
 * Sırasız çift mi? Yalnız "İkili - Sabit ihtimalli" (çift seçimli ama soneksiz). Sıralı İkili'de
 * sıra bahsin kendisidir (EX soneği), Kim Geçer'de de sıra anlamlıdır (önce oynanan at, sonra
 * rakip) - ikisinde de sıralama yapılmaz.
 */
function isUnorderedFobPair(bettingType: any): boolean {
    const definition = BettingTypesMap[Number(bettingType)];
    return !!definition?.isFobPairPick && !definition?.suffix;
}

/** Pazar/gönderim sırası: sırasız İkili'de küçük numara önce, diğer türlerde verilen sıra. */
export function canonicalFobPairOrder(bettingType: any, firstNo: any, secondNo: any): string[] {
    return isUnorderedFobPair(bettingType)
        ? [String(Math.min(Number(firstNo), Number(secondNo))), String(Math.max(Number(firstNo), Number(secondNo)))]
        : [String(firstNo), String(secondNo)];
}

/** Kupon ayaklarına yazılacak at sırası: seçim sırası değil, pazarın kanonik sırası. */
export function fobSubmitHorseNos(item: FobSlipItem): string[] {
    return item.horseNos.length === 2
        ? canonicalFobPairOrder(item.bettingType, item.horseNos[0], item.horseNos[1])
        : item.horseNos;
}

interface FobSlipOptions {
    multiply: number;
    usePointBalance: boolean;
    shareCoupon: boolean;
    acceptChangedOdds: boolean;
    fobSystemSelected: number[];
    errorMessage: string | null;
    inProgress: boolean;
}
const defaultFobSlipOptions: FobSlipOptions = {
    multiply: 20,
    usePointBalance: false,
    shareCoupon: false,
    acceptChangedOdds: true,
    fobSystemSelected: [],
    errorMessage: null,
    inProgress: false,
};

interface FobPendingPick {
    hippodrome: any;
    run: any;
    bettingType: number;
    first: any;
    second: any;
}

const fobBetslipSubject = new BehaviorSubject<FobSlipItem[]>([]);
const fobSlipOptionsSubject = new BehaviorSubject<FobSlipOptions>(defaultFobSlipOptions);
const pendingFobPicksSubject = new BehaviorSubject<FobPendingPick[]>([]);

function combinations<T>(arr: T[], k: number): T[][] {
    if (k === 0) return [[]];
    if (arr.length < k) return [];
    const [first, ...rest] = arr;
    const withFirst = combinations(rest, k - 1).map((c) => [first, ...c]);
    const withoutFirst = combinations(rest, k);
    return [...withFirst, ...withoutFirst];
}

export function getFobItemDisplayOdds(item: FobSlipItem): string | "KO" {
    const winOdds = Number(resolveFobItemOddsEntry(item)?.win_odds);
    return (!winOdds || winOdds === 1 || BettingTypesMap[item.bettingType]?.isClosingOdds) ? "KO" : winOdds.toFixed(2);
}

function resolveFobItemOddsEntry(item: FobSlipItem): any {
    const isH2H = !!BettingTypesMap[item.bettingType]?.isHeadToHead;
    if (isH2H && item.run?.fob_h2h) {
        const [selectedNo, opponentNo] = item.horseNos;
        for (const pair of Object.values(item.run.fob_h2h) as any[]) {
            const hasOpponent = pair?.some((e: any) => String(e?.horse_no) === opponentNo);
            if (!hasOpponent) continue;
            const found = pair?.find((e: any) => String(e?.horse_no) === selectedNo);
            if (found) return found;
        }
        return undefined;
    }

    return fobOddsEntry(item.run, item.bettingType, item.oddKey);
}

export function isFobItemPlayable(item: FobSlipItem): boolean {
    const entry = resolveFobItemOddsEntry(item);
    if (entry?.show === false || entry?.bettable === false) {
        return false;
    }
    const hasLiveOdds = entry?.show && entry?.win_odds && Number(entry.win_odds) > 1;
    return !!(hasLiveOdds || entry?.status === 'o');
}

export function isFobPendingPickPlayable(pending: FobPendingPick): boolean {
    const horseNo = pending.first ?? pending.second;
    const entry = pending.run?.event_odds?.[String(horseNo)];
    const hasLiveOdds = entry?.show && entry?.win_odds && Number(entry.win_odds) > 1;
    return !!(hasLiveOdds || entry?.status === 'o');
}

export function resolveFobItemHorses(item: FobSlipItem) {
    const [firstNo, secondNo] = item.horseNos;
    return {
        isPair: item.horseNos.length === 2,
        primaryHorse: item.run?.horseMap?.[firstNo],
        secondHorse: secondNo ? item.run?.horseMap?.[secondNo] : null,
        label: item.horseNos.join('-'),
    };
}

class FobBetslipService {
    private items: FobSlipItem[] = [];
    private options: FobSlipOptions = {...defaultFobSlipOptions};
    private pendingPicks: FobPendingPick[] = [];

    getAll(): FobSlipItem[] {
        return this.items;
    }

    getPendingPicks(): FobPendingPick[] {
        return this.pendingPicks;
    }

    getPendingPickFor(hippodrome: any, run: any, bettingType: number): FobPendingPick | undefined {
        return this.pendingPicks.find((p) =>
            p.hippodrome._id === hippodrome._id &&
            p.run.no === run.no &&
            p.bettingType === bettingType
        );
    }

    setPendingPick(pick: FobPendingPick) {
        this.pendingPicks = this.pendingPicks.filter((p) => !(
            p.hippodrome._id === pick.hippodrome._id &&
            p.run.no === pick.run.no &&
            p.bettingType === pick.bettingType
        ));
        this.pendingPicks.push(pick);
        this.updatePending();
    }

    clearPendingPick(hippodrome: any, run: any, bettingType: number) {
        this.pendingPicks = this.pendingPicks.filter((p) => !(
            p.hippodrome._id === hippodrome._id &&
            p.run.no === run.no &&
            p.bettingType === bettingType
        ));
        this.updatePending();
    }

    getOptions(): FobSlipOptions {
        return this.options;
    }

    isAdded(item: FobSlipItem): number | false {
        const idx = this.items.findIndex((i) =>
            i.hippodrome._id === item.hippodrome._id &&
            i.run.no === item.run.no &&
            i.oddKey === item.oddKey &&
            i.bettingType === item.bettingType
        );
        return idx === -1 ? false : idx;
    }

    add(item: FobSlipItem) {
        try {
            const isH2HItem = !!BettingTypesMap[item.bettingType]?.isHeadToHead;
            this.items = this.items.filter((i) => {
                if (i.hippodrome._id !== item.hippodrome._id || i.run.no !== item.run.no) {
                    return true;
                }
                return isH2HItem
                    && !!BettingTypesMap[i.bettingType]?.isHeadToHead
                    && [...i.horseNos].sort().join('-') !== [...item.horseNos].sort().join('-');
            });

            if (this.items.length >= FOB_MAX_BET_COUNT) {
                this.update();
                modalServiceSubject.next({
                    title: "Uyarı",
                    content: `Kombine Sabit İhtimalli Bahis Türünde en fazla ${FOB_MAX_BET_COUNT} at ekliyebilirsiniz.`,
                    timeout: 3000,
                    confirm: {
                        cancel: {
                            label: "Tamam",
                        },
                    },
                });
                return
            }

            this.items.push(item);
        } catch (error) {
            console.error(error);
            return {ok: false};
        } finally {
            this.update();
        }
    }

    remove(item: FobSlipItem) {
        this.items = this.items.filter((i) => !(
            i.hippodrome._id === item.hippodrome._id &&
            i.run.no === item.run.no &&
            i.oddKey === item.oddKey &&
            i.bettingType === item.bettingType
        ));
        this.update();
    }

    toggle(item: FobSlipItem) {
        const existingIndex = this.isAdded(item);
        if (existingIndex !== false) {
            this.remove(item);
            return {ok: true};
        }
        return this.add(item);
    }

    clear() {
        this.items = [];
        this.update();
        this.pendingPicks = [];
        this.updatePending();
        this.setOptions({multiply: 20, usePointBalance: false, shareCoupon: false, errorMessage: null});
    }

    setOptions(patch: Partial<FobSlipOptions>) {
        this.options = {...this.options, ...patch};
        this.updateOptions();
    }

    getSystemOptionsCount(): number | null {
        const minimumBetCount = FOB_SYSTEM_MIN_BET_COUNT;
        const maximumBetCount = FOB_SYSTEM_MAX_BET_COUNT;
        const currentBetCount = this.items.length;

        if (currentBetCount < minimumBetCount || currentBetCount > maximumBetCount) {
            return null;
        }

        return currentBetCount;
    }

    pruneSystemSelection() {
        const currentBetCount = this.getSystemOptionsCount();
        const validSelected = currentBetCount === null
            ? []
            : this.options.fobSystemSelected.filter((k) => k <= currentBetCount);

        if (validSelected.length !== this.options.fobSystemSelected.length) {
            this.options = {...this.options, fobSystemSelected: validSelected};
            this.updateOptions();
        }
    }

    toggleSystemOption(option: number) {
        this.options = {
            ...this.options,
            fobSystemSelected: this.options.fobSystemSelected.includes(option)
                ? this.options.fobSystemSelected.filter((k) => k !== option)
                : [...this.options.fobSystemSelected, option],
        };
        this.updateOptions();
    }

    getSystemCombinations(): FobSlipItem[][] {
        return this.options.fobSystemSelected.flatMap((k) => combinations(this.items, k));
    }

    getTotalAmount(): number {
        if (!this.items.length) return 0;
        return this.options.multiply * (this.getSystemCombinations().length || 1);
    }

    // `isClosing`: bacaklardan biri kapanış oranıysa (KO) toplam oran bilinmiyor demektir -
    // `odds || 1` ile hesaplanan rakam o bacağı yok sayacağı için tutar YERİNE "KO" gösterilmeli.
    getMinMax(): { minWin: number; maxWin: number; isClosing: boolean } {
        const isClosing = this.hasClosingOdds();
        if (!this.items.length) {
            return {minWin: 0, maxWin: 0, isClosing};
        }

        const combos = this.options.fobSystemSelected.length ? this.getSystemCombinations() : [this.items];
        const multiply = Number(this.options.multiply);

        const comboOdds = combos.map((combo) => combo.reduce((acc: number, item: FobSlipItem) => {
            const odds = Number(resolveFobItemOddsEntry(item)?.win_odds);
            return acc * (odds || 1);
        }, 1));

        // Bayat sistem seçimi (k > bacak sayısı) hiç kombinasyon üretmiyor; boş dizide
        // `Math.min(...)` Infinity döndürüyordu.
        if (comboOdds.length === 0) {
            return {minWin: 0, maxWin: 0, isClosing};
        }

        const maxWin = comboOdds.reduce((a, b) => a + b, 0) * multiply;
        const minWin = Math.min(...comboOdds) * multiply;

        return {minWin, maxWin, isClosing};
    }

    // Oranı henüz kesin olmayan (kapanış oranı / KO) bacak var mı?
    hasClosingOdds(): boolean {
        return this.items.some((item) => {
            const odds = Number(resolveFobItemOddsEntry(item)?.win_odds);
            return !odds || odds === 1;
        });
    }

    // KO bacak bilgiyi yok etmiyor: toplam, oranı BİLİNEN bacakların çarpımı olarak dönüyor ve
    // gösterimde "+ KO" ile işaretleniyor. Hiçbir bacağın oranı bilinmiyorsa "KO".
    getTotalOdds(): number | "KO" {
        let total = 1;
        let known = 0;
        for (const item of this.items) {
            const odds = Number(resolveFobItemOddsEntry(item)?.win_odds);
            if (!odds || odds === 1) continue;
            total *= odds;
            known += 1;
        }
        return known > 0 ? total : "KO";
    }

    // `imgData`: kuponun ekran görüntüsü (data URL). Müşterekteki desenin aynısı - çağıran
    // html2canvas ile üretip veriyor, üretemezse boş string geliyor ve alan yine de gönderiliyor.
    async submit(save: boolean, imgData: any = ""): Promise<any> {
        if (this.options.inProgress || this.items.length === 0) {
            return null;
        }

        this.setOptions({inProgress: true});

        try {
            const couponData = this.buildCouponData();
            return await postCoupon({
                coupon: couponData,
                save,
                usePointBalance: this.options.usePointBalance,
                shareCoupon: this.options.shareCoupon,
                couponImage: imgData,
            });
        } catch (error: any) {
            return toSubmitError(error);
        } finally {
            this.setOptions({inProgress: false});
        }
    }

    private buildCouponData() {
        const isCombine = this.items.length > 1;
        if (isCombine) {
            const price = this.getTotalAmount();
            const oddIds = this.items
                .map((item) => resolveFobItemOddsEntry(item)?.id?.toString())
                .join('|');

            const couponData: any = {
                betCount: 1,
                betType: BettingTypeIds.combine,
                cardId: this.items[0].hippodrome.card_id,
                race: this.items[0].run.no.toString(),
                multiplier: Number(this.options.multiply),
                system: false,
                price: price.toFixed(2),
                legs: this.buildCouponLegs(),
                rows: this.buildCouponRows(),
                poolUnit: Math.round(Number(BettingTypesMap[this.items[0].bettingType]?.price ?? 1) * 100),
                systemAmount: 5,
                fob: true,
                oddRatio: 1,
                oddId: oddIds,
            };

            if (this.options.fobSystemSelected.length > 0) {
                couponData.betType = BettingTypeIds.combineSystem;
                couponData.fobSystemSelected = [...this.options.fobSystemSelected].sort((a, b) => a - b);
                couponData.betCount = this.getSystemCombinations().length || 1;
            }

            return couponData;
        }

        const item = this.items[0];
        const price = Number(this.options.multiply);
        return {
            betCount: 1,
            betType: item.bettingType,
            cardId: item.hippodrome.card_id,
            race: item.run.no.toString(),
            multiplier: price,
            system: false,
            price: price.toFixed(2),
            legs: this.buildCouponLegs(),
            rows: this.buildCouponRows(),
            poolUnit: Math.round(Number(BettingTypesMap[item.bettingType]?.price ?? 1) * 100),
            systemAmount: 5,
            fob: true,
            oddRatio: resolveFobItemOddsEntry(item)?.win_odds?.toString(),
            oddId: resolveFobItemOddsEntry(item)?.id?.toString(),
        };
    }

    private buildCouponLegs() {
        const isCombine = this.items.length > 1;

        if (isCombine) {
            return this.items.map((item) => ({
                [`${item.hippodrome._date}-${item.hippodrome.keyword.toLowerCase()}_${item.run.no}_${item.bettingType}`]:
                    [BettingTypesMap[item.bettingType]?.isHeadToHead ? `${item.oddKey}H2H` : item.oddKey],
            }));
        }


        const item = this.items[0];
        return item.horseNos.length === 2
            ? fobSubmitHorseNos(item).map((no) => [no])
            : [[item.oddKey]];
    }

    private buildCouponRows() {
        return this.items.map((item) => {
            const oddEntry = resolveFobItemOddsEntry(item);
            if (!isFobItemPlayable(item)) {
                throw new Error('Seçilen bahislerden biri artık geçerli değil (koşu kapanmış olabilir). Lütfen sepetinizdeki işaretli seçimi kaldırıp tekrar deneyin.');
            }
            if (oddEntry?.win_odds == null) {
                throw new Error('Seçilen bahislerden birinin oranı okunamadı. Lütfen sepetinizdeki seçimi kaldırıp tekrar deneyin.');
            }
            return {
                bet_type: Number(item.bettingType),
                card_id: Number(item.hippodrome.card_id),
                card: `${item.hippodrome._date}-${item.hippodrome.keyword.toLowerCase()}`,
                race_id: Number(item.run.no),
                odd_key: item.oddKey,
                odd_ratio: oddEntry.win_odds.toString(),
                odd_id: Number(oddEntry.id),
                is_closing_odd: !(Number(oddEntry.win_odds)) || (Number(oddEntry.win_odds) === 1),
                hippodrome_name: item.hippodrome.hippodrome,
            };
        });
    }

    private update() {
        this.pruneSystemSelection();
        fobBetslipSubject.next(this.items);
    }

    private updateOptions() {
        fobSlipOptionsSubject.next(this.options);
    }

    private updatePending() {
        pendingFobPicksSubject.next(this.pendingPicks);
    }
}

export const FOBBetslip = new FobBetslipService();

export function useFobBetslip(): FobSlipItem[] {
    return useSubjectSnapshot(fobBetslipSubject, () => FOBBetslip.getAll());
}

export function useFobSlipOptions(): FobSlipOptions {
    return useSubjectSnapshot(fobSlipOptionsSubject, () => FOBBetslip.getOptions());
}

export function useFobPendingPicks(): FobPendingPick[] {
    return useSubjectSnapshot(pendingFobPicksSubject, () => FOBBetslip.getPendingPicks());
}

export function resolveFobCurrentPick(hippodrome: any, run: any, bettingType: number): { first: any; second: any } {
    const pendingPick = FOBBetslip.getPendingPickFor(hippodrome, run, bettingType);
    if (pendingPick) {
        return {first: pendingPick.first, second: pendingPick.second};
    }

    const item = FOBBetslip.getAll().find((i) =>
        i.hippodrome._id === hippodrome._id &&
        i.run.no === run.no &&
        i.bettingType === bettingType
    );

    if (item) {
        return {first: item.horseNos[0] ?? null, second: item.horseNos[1] ?? null};
    }

    return {first: null, second: null};
}
